/**
 * @file BenefitPlanList.js
 * @description Displays a list of benefit plans with search, filter, and sorting functionality.
 */

import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './PlanYear.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import {
    searchBenefitPlans,
    deleteBenefitPlan,
    fetchBenefitPlanById,
    createBenefitPlan,
    updateBenefitPlan,
    planDetailReport,
} from '../../services/benefitPlanService';
import { getNameForDuplicate } from '../../utils/utils';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import PaginationComponent from '../helpers/PaginationComponent';
import SearchComponent from '../helpers/search/SearchComponent';
import { downloadReport } from './PlanDetailReport';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortOptions = {
    DEFAULT: 'default',
    NAME: 'name',
    OVERRIDE_NAME: 'overrideName',
    PLAN_TYPE: 'planType',
    CODE: 'code',
    NETWORK: 'network',
    CARRIER: 'carrier',
    POLICY: 'policy',
    IS_ACTIVE: 'isActive',
    IS_VISIBLE: 'isVisible',
};

const OrderOptions = {
    DEFAULT: 'default',
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ITEMS_PER_PAGE = 20;
const ALL_BENEFIT_PLANS_COUNT = 1000;

const BenefitPlanList = () => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [benefitPlanList, setBenefitPlanList] = useState([]);
    const [allBenefitPlanList, setAllBenefitPlanList] = useState([]);
    const [filteredBenefitPlanList, setFilteredBenefitPlanList] = useState([]);
    const [selectedBenefitPlanIds, setSelectedBenefitPlanIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        sort: SortOptions.DEFAULT,
        order: OrderOptions.DEFAULT,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [isUpdatingEnabled, setIsUpdatingEnabled] = useState(false);
    const [isUpdatingVisible, setIsUpdatingVisible] = useState(false);
    const hasActiveFilters = Object.keys(selections).length > 0;

    const selectAllRef = useRef(null);

    // fetch all benefit plans on page render
    useEffect(() => {
        loadAllBenefitPlans();
    }, []);

    useEffect(() => {
        if (!initialLoadComplete) return;
        loadAllBenefitPlans();
    }, [sortConfig.sort, sortConfig.order]);

    useEffect(() => {
        if (!initialLoadComplete) return;
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // apply filter, search, and sort to create the complete filtered dataset
    useEffect(() => {
        if (allBenefitPlanList.length === 0) return;
        applyFilterSearchAndSort();
        setCurrentPage(1);
    }, [allBenefitPlanList, selections, debouncedSearchTerm, sortConfig]);

    // paginate the filtered dataset
    useEffect(() => {
        loadBenefitPlans();
    }, [currentPage, filteredBenefitPlanList]);

    const loadAllBenefitPlans = async () => {
        try {
            setIsLoading(true);
            const allBenefitPlansResponse = await searchBenefitPlans(
                null,
                currentPage,
                ALL_BENEFIT_PLANS_COUNT,
                searchTerm,
                sortConfig.sort,
                sortConfig.order,
            );
            setAllBenefitPlanList(allBenefitPlansResponse.viewModels);
        } catch (error) {
            console.error(
                'Failed to fetch benefit plan list for all plans',
                error,
            );
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else
                Toast.error(
                    getText('msg_b0c804ca79bb'),
                );
        } finally {
            setIsLoading(false);
            setInitialLoadComplete(true);
        }
    };

    const applyFilterSearchAndSort = () => {
        let result = [...allBenefitPlanList];

        // apply filters
        if (hasActiveFilters) {
            Object.entries(selections).forEach(([column, selectedValues]) => {
                if (selectedValues && selectedValues.length > 0) {
                    result = result.filter(benefitPlan => {
                        if (column === 'isActive') {
                            const value = benefitPlan.isActive ? 'Yes' : 'No';
                            return selectedValues.includes(value);
                        }
                        if (column === 'planType') {
                            return selectedValues.includes(
                                benefitPlan.planType?.name,
                            );
                        }
                        if (column === 'carrier') {
                            return selectedValues.includes(
                                benefitPlan.vendor?.name,
                            );
                        }
                        if (column === 'networkType') {
                            return selectedValues.includes(
                                benefitPlan.networkType?.name,
                            );
                        }
                        return selectedValues.includes(
                            benefitPlan[column]?.toString(),
                        );
                    });
                }
            });
        }

        // apply search term
        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase();
            result = result.filter(
                plan =>
                    plan.name?.toLowerCase().includes(searchLower) ||
                    plan.code?.toLowerCase().includes(searchLower) ||
                    plan.overrideName?.toLowerCase().includes(searchLower) ||
                    plan.planType?.name?.toLowerCase().includes(searchLower) ||
                    plan.vendor?.name?.toLowerCase().includes(searchLower) ||
                    plan.policy?.toLowerCase().includes(searchLower) ||
                    plan.networkType?.name?.toLowerCase().includes(searchLower),
            );
        }

        // apply sorting
        if (sortConfig.sort && sortConfig.sort !== SortOptions.DEFAULT) {
            result.sort((a, b) => {
                let aValue, bValue;

                if (sortConfig.sort === 'planType') {
                    aValue = a.planType?.name || '';
                    bValue = b.planType?.name || '';
                } else if (sortConfig.sort === 'carrier') {
                    aValue = a.vendor?.name || '';
                    bValue = b.vendor?.name || '';
                } else if (sortConfig.sort === 'network') {
                    aValue = a.networkType?.name || '';
                    bValue = b.networkType?.name || '';
                } else if (sortConfig.sort === 'isActive') {
                    aValue = a.isActive ? 1 : 0;
                    bValue = b.isActive ? 1 : 0;
                } else if (sortConfig.sort === 'isVisible') {
                    aValue = a.isVisible ? 1 : 0;
                    bValue = b.isVisible ? 1 : 0;
                } else {
                    aValue = a[sortConfig.sort] || '';
                    bValue = b[sortConfig.sort] || '';
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.order === OrderOptions.ASCENDING ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.order === OrderOptions.ASCENDING ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredBenefitPlanList(result);
        const newTotalPages = Math.ceil(result.length / ITEMS_PER_PAGE);
        setTotalPages(newTotalPages);
    };

    const loadBenefitPlans = () => {
        setIsLoading(true);
        try {
            if (
                filteredBenefitPlanList.length === 0 &&
                !hasActiveFilters &&
                !debouncedSearchTerm
            ) {
                // no data to paginate, don't update
                setBenefitPlanList([]);
                setFilteredData([]);
            } else {
                // paginate from the filtered/searched/sorted list
                const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                const endIndex = startIndex + ITEMS_PER_PAGE;
                const paginatedResults = filteredBenefitPlanList.slice(
                    startIndex,
                    endIndex,
                );
                setBenefitPlanList(paginatedResults);
                setFilteredData(paginatedResults);
            }
        } catch (error) {
            console.error('Failed to paginate benefit plan list', error);
            Toast.error(getText('msg_3db8e436b31b'));
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSortConfig({
            sort: SortOptions.DEFAULT,
            order: OrderOptions.DEFAULT,
        });
        setCurrentPage(1);
        setFilteredBenefitPlanList([]);
    };

    const getOptions = column => {
        return Array.from(
            new Set(
                allBenefitPlanList.map(item => {
                    if (column === 'isActive') {
                        return item.isActive ? 'Yes' : 'No';
                    }
                    if (column === 'isVisible') {
                        return item.isVisible ? 'Yes' : 'No';
                    }
                    if (column === 'planType') {
                        return item.planType?.name;
                    }
                    if (column === 'carrier') {
                        return item.vendor?.name;
                    }
                    if (column === 'networkType') {
                        return item.networkType?.name;
                    }
                    return item[column]?.toString();
                }),
            ),
        )
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const handleSearch = term => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleSort = sort => {
        setSortConfig({
            sort,
            order:
                sortConfig.sort === sort &&
                sortConfig.order === OrderOptions.ASCENDING
                    ? OrderOptions.DESCENDING
                    : OrderOptions.ASCENDING,
        });
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        let deletedCount = 0;
        const errors = [];

        for (const benefitPlanId of selectedBenefitPlanIds) {
            try {
                await deleteBenefitPlan(benefitPlanId);
                deletedCount++;
            } catch (error) {
                console.error(
                    `Failed to delete benefit plan with ID ${benefitPlanId}:`,
                    error,
                );
                if (error?.response?.data?.detail)
                    errors.push(error?.response?.data?.detail);
                else errors.push(benefitPlanId);
            }
        }

        if (deletedCount > 0) {
            await loadAllBenefitPlans();
        }

        if (deletedCount === selectedBenefitPlanIds.length) {
            Toast.success(
                `${deletedCount} benefit plan(s) deleted successfully`,
            );
        } else if (deletedCount > 0) {
            Toast.warning(
                `${deletedCount} out of ${selectedBenefitPlanIds.length} benefit plan(s) deleted successfully. Some deletions failed.`,
            );
        } else {
            Toast.error(getText('msg_cf9ee96880de'));
        }

        setSelectedBenefitPlanIds([]);
    };

    const handleDuplicate = async () => {
        if (selectedBenefitPlanIds.length !== 1) {
            Toast.error(getText('msg_f7056354e225'));
            return;
        }

        try {
            const [benefitPlanId] = selectedBenefitPlanIds;
            const benefitPlanToDuplicate =
                await fetchBenefitPlanById(benefitPlanId);

            if (!benefitPlanToDuplicate) {
                throw new Error('Selected benefit plan not found');
            }

            const allBenefitPlansResponse = await searchBenefitPlans(
                null,
                1,
                ALL_BENEFIT_PLANS_COUNT,
                '',
                'name',
                'asc',
            );

            const allBenefitPlans = allBenefitPlansResponse.viewModels || [];
            const benefitPlanNames = allBenefitPlans.map(plan => plan.name);
            const benefitPlanCodes = allBenefitPlans.map(plan => plan.code);

            const newName = getNameForDuplicate(
                benefitPlanToDuplicate.name,
                benefitPlanNames,
            );

            const newCode = getNameForDuplicate(
                benefitPlanToDuplicate.code,
                benefitPlanCodes,
            );

            const newOverrideName = benefitPlanToDuplicate.overrideName
                ? getNameForDuplicate(
                      benefitPlanToDuplicate.overrideName,
                      allBenefitPlans
                          .map(plan => plan.overrideName)
                          .filter(Boolean),
                  )
                : null;

            const newBenefitPlan = {
                ...benefitPlanToDuplicate,
                id: 0,
                name: newName,
                code: newCode,
                overrideName: newOverrideName,
            };

            await createBenefitPlan(newBenefitPlan);
            await loadAllBenefitPlans();
            Toast.success(getText('msg_ac4d9e05dc9e'));
            setSelectedBenefitPlanIds([]);
        } catch (error) {
            console.error('Failed to duplicate benefit plan', error);
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else
                Toast.error(getText('msg_470e6c42384d'));
        }
    };

    const handleBenefitPlanSelection = benefitPlanId => {
        setSelectedBenefitPlanIds(prevSelection =>
            prevSelection.includes(benefitPlanId)
                ? prevSelection.filter(id => id !== benefitPlanId)
                : [...prevSelection, benefitPlanId],
        );
    };

    const handleSelectAllChange = event => {
        setSelectedBenefitPlanIds(
            event.target.checked ? filteredData.map(plan => plan.id) : [],
        );
    };

    const handleViewBenefitPlan = id => {
        navigate(ROUTE_BUILDERS.benefitPlansView(id));
    };

    const handleAddBenefitPlan = () => {
        navigate(ROUTES.BENEFIT_PLANS_ADD);
    };

    const openDeleteConfirmationModal = () => {
        if (!selectedBenefitPlanIds.length) {
            Toast.error(getText('msg_9bd1b9956da8'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            text: `This action will delete ${
                selectedBenefitPlanIds.length > 1
                    ? 'these benefit plans'
                    : 'this benefit plan'
            } and cannot be undone.`,
            onYes: () => handleDelete(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Benefit plans delete clicked`);
    };

    const handleToggleEnabled = async benefitPlan => {
        const newEnabledState = !benefitPlan.isActive;
        const actionText = newEnabledState ? 'enable' : 'disable';

        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_410bf23a3f08'),
            text: `This will ${actionText} the "${benefitPlan.name}"?`,
            onYes: () => updatePlanEnabledStatus(benefitPlan, newEnabledState),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_04a212215ef9'),
        });
    };

    const handleToggleVisible = async benefitPlan => {
        const newVisibleState = !benefitPlan.isVisible;
        // Directly update visibility without confirmation
        // request: removing bidirectional confirmation to avoid extra UX
        // const actionText = newVisibleState ? 'show' : 'hide';
        // showModal({
        //     type: ModalTypes.ACTION,
        //     title: `Are you sure?`,
        //     text: `This will ${actionText} the "${benefitPlan.name}"?`,
        //     onYes: () => updatePlanVisibleStatus(benefitPlan, newVisibleState),
        //     onNo: () => {},
        //     noButtonText: 'Cancel',
        //     yesButtonText: 'Confirm',
        // });
        updatePlanVisibleStatus(benefitPlan, newVisibleState);
    };

    const updatePlanVisibleStatus = async (benefitPlan, newVisibleState) => {
        if (isUpdatingVisible) return;

        setIsUpdatingVisible(true);
        try {
            const updatedPlan = {
                ...benefitPlan,
                isVisible: newVisibleState,
            };

            await updateBenefitPlan(updatedPlan);

            setAllBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isVisible: newVisibleState }
                        : plan,
                ),
            );

            setFilteredBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isVisible: newVisibleState }
                        : plan,
                ),
            );

            setBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isVisible: newVisibleState }
                        : plan,
                ),
            );

            setFilteredData(prevData =>
                prevData.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isVisible: newVisibleState }
                        : plan,
                ),
            );

            Toast.success(
                `Benefit plan ${newVisibleState ? 'shown' : 'hidden'} successfully`,
            );
        } catch (error) {
            console.error(
                'Failed to update benefit plan visibility status',
                error,
            );
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else
                Toast.error(
                    `There was an issue ${newVisibleState ? 'showing' : 'hiding'} the benefit plan.`,
                );
        } finally {
            setIsUpdatingVisible(false);
        }
    };

    const updatePlanEnabledStatus = async (benefitPlan, newEnabledState) => {
        if (isUpdatingEnabled) return;

        setIsUpdatingEnabled(true);
        try {
            const updatedPlan = {
                ...benefitPlan,
                isActive: newEnabledState,
            };

            await updateBenefitPlan(updatedPlan);

            setAllBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isActive: newEnabledState }
                        : plan,
                ),
            );

            setFilteredBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isActive: newEnabledState }
                        : plan,
                ),
            );

            setBenefitPlanList(prevList =>
                prevList.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isActive: newEnabledState }
                        : plan,
                ),
            );

            setFilteredData(prevData =>
                prevData.map(plan =>
                    plan.id === benefitPlan.id
                        ? { ...plan, isActive: newEnabledState }
                        : plan,
                ),
            );

            Toast.success(
                `Benefit plan ${newEnabledState ? 'enabled' : 'disabled'} successfully`,
            );
        } catch (error) {
            console.error(
                'Failed to update benefit plan enabled status',
                error,
            );
            if (error?.response?.data?.detail)
                Toast.error(error?.response?.data?.detail);
            else
                Toast.error(
                    `There was an issue ${
                        newEnabledState ? 'enabling' : 'disabling'
                    } the benefit plan.`,
                );
        } finally {
            setIsUpdatingEnabled(false);
        }
    };

    const renderSortArrow = columnKey => {
        if (
            (sortConfig.sort === 'default' && columnKey === 'isActive') ||
            sortConfig.sort === columnKey
        ) {
            return (
                <span className="mx-2">
                    <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                        <path
                            d={
                                sortConfig.order === OrderOptions.DESCENDING
                                    ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                    : 'M1 0.75L5.25 5.25L9.5 0.75'
                            }
                            stroke="#2F2B43"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            );
        } else {
            return null;
        }
    };

    const downloadPlanDetailReport = async () => {
        try {
            setIsLoading(true);
            downloadReport();
        } catch (error) {
            console.error('Error downloading report', error);
            Toast.error(getText('msg_821613944fc1'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <div className="float-start d-flex align-items-center d-flex flex-direction-column align-items-baseline">
                            <p className="page-title card-title ms-4 me-5 mb-0">
                                {getText('msg_b80f31aa5552')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_ff4b941ab5e7')}
                                <br />
                                {getText('msg_cc6112963b71')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center align-self-baseline">
                            <span
                                type="button"
                                onClick={openDeleteConfirmationModal}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                            <span
                                type="button"
                                onClick={handleDuplicate}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-copy me-2" />
                                {getText('msg_972d57379db3')}
                            </span>
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <span
                                type="button"
                                onClick={() => downloadPlanDetailReport()}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_ee45c30326b7')}
                            </span>
                            <span
                                type="button"
                                onClick={handleAddBenefitPlan}
                                className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </span>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center my-5 px-4">
                        <SearchComponent
                            onSearch={handleSearch}
                            placeholder={getText('msg_bce06414177f')}
                            initialValue={searchTerm}
                        />
                    </div>

                    {benefitPlanList.length === 0 && isLoading ? (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255,255,255,0.6)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 10,
                            }}
                        >
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : (
                        <div className="table-responsive-xxl w-100">
                            <table className="table table-hover align-middle">
                                <colgroup>
                                    <col className={styles.benefitPlanColSelect} />
                                    <col
                                        className={styles.benefitPlanColPlanType}
                                    />
                                    <col className={styles.benefitPlanColName} />
                                    <col
                                        className={
                                            styles.benefitPlanColOverrideName
                                        }
                                    />
                                    <col className={styles.benefitPlanColCode} />
                                    <col
                                        className={styles.benefitPlanColCarrier}
                                    />
                                    <col
                                        className={styles.benefitPlanColVisible}
                                    />
                                </colgroup>
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th
                                            className={`text-center checkbox-column-head`}
                                        >
                                            <input
                                                type="checkbox"
                                                className={`form-check-input`}
                                                ref={selectAllRef}
                                                checked={
                                                    selectedBenefitPlanIds.length ===
                                                        filteredData.length &&
                                                    filteredData.length > 0
                                                }
                                                onChange={handleSelectAllChange}
                                            />
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleSort(
                                                    SortOptions.PLAN_TYPE,
                                                )
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_45061ce8ef30')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.PLAN_TYPE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('planType');
                                                }}
                                                className={styles.cursorPointer}
                                            >
                                                {renderFilterIcon(
                                                    selections['planType']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeModal === 'planType' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                    isRightColumn={false}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(SortOptions.NAME)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_709a23220f2c')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.NAME,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('name');
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['name']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'name' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                    isRightColumn={false}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            className="position-relative"
                                            onClick={() =>
                                                handleSort(
                                                    SortOptions.OVERRIDE_NAME,
                                                )
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_e2e836862394')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.OVERRIDE_NAME,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('overrideName');
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['overrideName']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeModal === 'overrideName' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                    isRightColumn={false}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleSort(SortOptions.CODE)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_adac69379a62')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.CODE,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('code');
                                                }}
                                                className={styles.cursorPointer}
                                            >
                                                {renderFilterIcon(
                                                    selections['code']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeModal === 'code' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                    isRightColumn={false}
                                                />
                                            )}
                                        </th>
                                        <th
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleSort(SortOptions.CARRIER)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_91b0ba24657f')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.CARRIER,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    openModal('carrier');
                                                }}
                                                className={styles.cursorPointer}
                                            >
                                                {renderFilterIcon(
                                                    selections['carrier']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeModal === 'carrier' && (
                                                <FilterModal
                                                    column={activeModal}
                                                    options={getOptions(
                                                        activeModal,
                                                    )}
                                                    selectedOptions={
                                                        selections[
                                                            activeModal
                                                        ] || []
                                                    }
                                                    onApply={handleApply}
                                                    onClose={closeModal}
                                                    isRightColumn={true}
                                                />
                                            )}
                                        </th>
                                        {/*
                                            NOTE: The implementation is left here commented for easy restoration
                                            Date: 2026-01-12

                                        <th
                                            style={{
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                            }}
                                            onClick={() =>
                                                handleSort(
                                                    SortOptions.IS_ACTIVE,
                                                )
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                Enable{' '}
                                                {renderSortArrow(
                                                    SortOptions.IS_ACTIVE,
                                                )}
                                            </span>
                                        </th>
                                        */}
                                        <th
                                            style={{
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                            }}
                                            onClick={() =>
                                                handleSort(
                                                    SortOptions.IS_VISIBLE,
                                                )
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_1fe59390acc1')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.IS_VISIBLE,
                                                )}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {benefitPlanList.length === 0 &&
                                        initialLoadComplete && (
                                            <EmptyTableRow
                                                colSpan={7}
                                                title={getText(
                                                    'msg_b9f30f168212',
                                                )}
                                                description={getText(
                                                    'msg_519d3b678d87',
                                                )}
                                            />
                                        )}
                                    {benefitPlanList.map(benefitPlan => (
                                        <tr key={benefitPlan.id}>
                                            <td className="text-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedBenefitPlanIds.includes(
                                                        benefitPlan.id,
                                                    )}
                                                    onChange={() =>
                                                        handleBenefitPlanSelection(
                                                            benefitPlan.id,
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td
                                                className={styles.titleCell}
                                                onClick={() =>
                                                    handleViewBenefitPlan(benefitPlan.id)
                                                }
                                                role="button"
                                                tabIndex={0}
                                            >
                                                {benefitPlan.planType?.name || ''}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {benefitPlan.name || ''}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {benefitPlan.overrideName || ''}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {benefitPlan.code || ''}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {benefitPlan.vendor?.name || ''}
                                            </td>
                                            {/*
                                              NOTE: The implementation is left here commented for easy restoration
                                              Date: 2026-01-12
                                            <td>
                                                <div
                                                    className="form-switch form-switch-locked"
                                                    style={{
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        marginLeft:
                                                            sortConfig.sort ===
                                                                'isActive' ||
                                                            sortConfig.sort ===
                                                                'default'
                                                                ? '-26px'
                                                                : '0px',
                                                    }}
                                                >
                                                    <input
                                                        className="form-check-input custom-switch-input"
                                                        type="checkbox"
                                                        checked={
                                                            benefitPlan.isActive
                                                        }
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            handleToggleEnabled(
                                                                benefitPlan,
                                                            );
                                                        }}
                                                        onClick={e =>
                                                            e.stopPropagation()
                                                        }
                                                        disabled={
                                                            isUpdatingEnabled
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            */}
                                            <td>
                                                <div
                                                    className="form-switch form-switch-locked"
                                                    style={{
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        className="form-check-input custom-switch-input"
                                                        type="checkbox"
                                                        checked={
                                                            !!benefitPlan.isVisible
                                                        }
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            handleToggleVisible(
                                                                benefitPlan,
                                                            );
                                                        }}
                                                        onClick={e =>
                                                            e.stopPropagation()
                                                        }
                                                        disabled={
                                                            isUpdatingVisible
                                                        }
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {benefitPlanList.length > 0 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default BenefitPlanList;
